import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {interval, Subscription} from "rxjs";
import * as _ from 'lodash';
import {UIRouterGlobals} from "@uirouter/angular";

import {DatabaseService} from "./database.service";
import {MenuService} from "./menu.service";
import {ParametersService} from "./parameters.service";
import {
    ControlService, HeatingCurveService,
    InputBoxService, ScheduleIntervalsService,
    ScheduleService,
    TilesService
} from "./tiles";
import {UserService} from "./user.service";
import {
    AlarmTile,
    MenuElement,
    Tile,
    TileContainer,
    TilesContainer,
    TilesData,
    UpdateResponse,
    UpdateZonesData,
    ZonesData, ZonesTileData,
} from "../models";
import {ZonesService} from "./zones.service";
import {ZonesSystemService} from "./zones-system.service";
import {TimeDateService} from "./time-date.service";

@Injectable({
    providedIn: 'root'
})
export class UpdateService implements OnDestroy {
    private interval: Subscription;
    lastUpdate: string | null = null;
    menuData: MenuElement[] = [];
    tilesData: TilesData[] | ZonesTileData[] = [];
    zonesData: UpdateZonesData;
    private menuElementsToTilesUpdate: MenuElement[] = [];

    constructor(
        private control: ControlService,
        private database: DatabaseService,
        private heatingCurve: HeatingCurveService,
        private http: HttpClient,
        private inputBox: InputBoxService,
        private menu: MenuService,
        private parameters: ParametersService,
        private schedule: ScheduleService,
        private scheduleIntervals: ScheduleIntervalsService,
        private tiles: TilesService,
        private timeDate: TimeDateService,
        private uiRouterGlobals: UIRouterGlobals,
        private user: UserService,
        private zones: ZonesService,
        private zonesSystem: ZonesSystemService,
    ) {
    }

    stopInterval() {
        if (this.interval) {
            this.interval.unsubscribe();
        }
    }

    startInterval() {
        if (this.parameters.selectedModuleHash === 0) return;

        if (!this.interval || this.interval.closed) {
            this.interval = interval(15000).subscribe(() => {
                this.updateData()
            });
        }
    }

    ngOnDestroy() {
        this.stopInterval();
    }

    updateData() {
        if (this.lastUpdate === null) {
            if (new Date(this.menu.lastUpdate).getTime() < new Date(this.database.lastUpdate).getTime()) {
                this.lastUpdate = this.menu.lastUpdate;
            } else {
                this.lastUpdate = this.database.lastUpdate;
            }
        }

        let alarmsIds = this.tiles.alarm !== null ? this.tiles.alarm.map(alarm => alarm.id) : [];
        if (this.tiles.withoutContainer.elements.length) {
            const alarmsFromTiles = this.tiles.withoutContainer.elements
                .filter(tiles => tiles.type === 'alarm')
                .map(alarm => alarm.id);
            alarmsIds = alarmsIds.concat(alarmsFromTiles);
        }

        const baseUrl = `/api/v1/users/${this.user.user_id}/modules/${this.parameters.selectedModuleHash}`;
        const lastUpdate = this.lastUpdate === null ? '' : `/last_update/${this.lastUpdate}`;
        const pinsArray = this.menu.getPinsForUpdate();
        const parents = `/parents/${JSON.stringify(pinsArray)}`;
        const alarmIdsParam = JSON.stringify(alarmsIds);
        const alarmIds = `/alarm_ids/${alarmIdsParam}`
        const path = `${baseUrl}/update/data${parents}${alarmIds}${lastUpdate}`;

        this.http.get<UpdateResponse>(path, {observe: 'response'}).subscribe((httpResponse) => {
            this.parameters.offline = false;
            const response = httpResponse.body;

            if (!httpResponse.url.includes(this.parameters.selectedModuleHash.toString())) {
                return;
            }

            if (response.init) {
                window.location.reload();
            }

            if (response.init === false) {
                this.menuData = response.menu;

                this.tilesData = response.tiles.filter((tile) => {
                    if (tile.type === 'alarm' && tile.toDelete) {
                        this.tiles.deleteAlarm(tile.id);
                        return false;
                    }
                    return true;
                });

                this.zonesData = response.zones;
                this.menu.parents = response.parents;
                this.lastUpdate = response.lastUpdate;
                this.parameters.selectedModule.moduleStatus = response.moduleStatus === 'active';
                this.parameters.selectedModule.controllerStatus = response.controllerStatus === 'active';

                if (!this.parameters.demoAccount()) {
                    if (this.menuData.length > 0) this.updateMenu();
                    if (this.tilesData.length > 0) this.updateTiles();
                    if (this.menuElementsToTilesUpdate.length > 0) this.updateTilesAfterMenuUpdate();
                    if (this?.zonesData?.elements.length > 0) this.updateZones(this.zonesData);
                    if (this?.zonesData?.globalSchedules.elements.length > 0) this.updateGlobalSchedules(this.zonesData.globalSchedules);
                    if (this?.zonesData?.controllerParameters !== undefined && this.zonesData.controllerParameters.controllerMode !== undefined) {
                        this.zones.zonesParameter = this.zonesData.controllerParameters.controllerMode;

                        const oldZonesData = this.database.zones.getValue();
                        if (Object.keys(oldZonesData).length > 0) {
                            const newZonesData = oldZonesData as ZonesData;
                            newZonesData.controllerParameters.controllerMode = this.zonesData.controllerParameters.controllerMode;
                            this.database.zones.next(newZonesData);
                        }
                    }

                    // updated system data only for a zones system controller
                    if (this?.zonesData?.elements.length === 0
                        && this.parameters.isZonesSystemControllerType()
                        && this.zonesData?.system !== undefined
                        && this.zonesData?.system.iterator !== null) {

                        const oldZonesData = this.database.zones.getValue();
                        if (Object.keys(oldZonesData).length > 0) {
                            const newZonesData = oldZonesData as ZonesData;
                            newZonesData.system = this.zonesData.system;
                            this.database.zones.next(newZonesData);
                        }

                        this.zonesSystem.updateSystemDataInSystemController();
                    }
                }
            }
        });
    }

    private updateZones(receivedZones: UpdateZonesData) {
        const oldZonesData = this.database.zones.getValue();
        const newZonesData = oldZonesData as ZonesData;

        receivedZones.elements.forEach((newElement) => {
            if (newElement !== null) {
                let updated = false;

                newZonesData.elements.forEach((oldElement, i) => {
                    if (!updated) {
                        if (oldElement !== null && oldElement.zone.id === newElement.zone.id) {
                            newZonesData.elements[i] = newElement;
                            updated = true;

                            if (newElement.zone.id === this.zones.currentlyOpenDropdown) {
                                this.zones.showHideDropdownList(this.zones.currentlyOpenDropdown);
                            }
                        }
                    }
                });

                if (!updated) {
                    const index = newElement.zone.index;
                    newZonesData.elements[index] = newElement;
                }
            }
        });

        this.database.zones.next(newZonesData);
        this.zones.checkNoActiveZones();

        if (this.parameters.isZonesSystemControllerType()) {
            if (receivedZones.system.iterator !== null) {
                const oldZonesData = this.database.zones.getValue();
                const newZonesData = oldZonesData as ZonesData;

                newZonesData.system = receivedZones.system;
                this.database.zones.next(newZonesData);
                this.zonesSystem.updateSystemDataInSystemController();
            }
            this.zonesSystem.updateZonesInSystemController();
        }
    }

    private updateGlobalSchedules(newGlobalSchedules: any) {
        const oldZonesData = this.database.zones.getValue();
        if (Object.keys(oldZonesData).length > 0) {
            const newZonesData = oldZonesData as ZonesData;
            newZonesData.globalSchedules = newGlobalSchedules;
            this.database.zones.next(newZonesData);
        }
    }

    private updateTiles() {
        this.tilesData = _.cloneDeep(this.tilesData.sort(this.type));
        this.tiles.createContainer = this.tiles.createContainer !== true
            ? this.checkCreateContainer()
            : this.tiles.createContainer;

        if (this.parameters.selectedModuleType === 'zones_controller') {
            this.updateTilesForZones();
        } else if (this.parameters.selectedModuleType === 'zones_system_controller') {
            const newTilesData = this.tilesData as ZonesTileData[];
            this.zonesSystem.updateTilesInSystemController(newTilesData);
        }
        else if (this.tiles.createContainer) {
            this.updateContainer();
        } else {
            this.updateWithoutContainer();
        }
    }

    private checkCreateContainer(): boolean {
        let createContainer = false;

        this.tilesData.forEach((val) => {
            if (val.type === 60) {
                createContainer = true;
            }
        });

        return createContainer;
    }

    private type(a: TilesData | ZonesTileData, b: TilesData | ZonesTileData) {
        if (a.type === 'alarm' || a.type === 'clear_alarm') return 1;
        if (b.type === 'alarm' || b.type === 'clear_alarm') return -1;
        if (a.type < b.type)
            return 1;
        if (a.type > b.type)
            return -1;
        return 0;
    }

    private updateTilesForZones() {
        this.tilesData.forEach((tile) => {
            this.addToDatabase(tile, 'add');
        });
    }

    private updateWithoutContainer(tiles?: TilesData[]) {
        const tilesTable = tiles ?? this.tilesData as TilesData[];
        let pushed = false;

        tilesTable.forEach((tile) => {
            pushed = false;

            if (tile.type !== 'clear_alarm') {
                tile.orderId = tile.type === 'alarm' ? -1 : tile.type === 3 ? 0 : tile.orderId;
            }

            this.tiles.withoutContainer.elements.forEach((val, key) => {
                tile = this.database.setTileOrder(tile) as Tile | AlarmTile;

                if (tile.id === val.id) {
                    pushed = true;
                    this.tiles.withoutContainer.elements[key] = tile;
                    this.addToDatabase(tile, 'add');
                }
            });

            if (!pushed && tile.visibility) {
                this.tiles.withoutContainer.expanded = true;
                this.tiles.withoutContainer.elements.push((tile as Tile | AlarmTile));
                const tilesData = this.database.tiles.getValue();
                if (Array.isArray(tilesData)) {
                    this.database.tiles.next(tilesData.concat([tile]));
                }
            }
        })
    }

    private updateContainer(tilesToUpdate?: TilesData[]) {
        let pushed = false;
        const newTiles = tilesToUpdate || this.tilesData as TilesData[];

        newTiles.forEach((tile: TilesData) => {
            tile = this.database.setTileOrder(tile) as TilesData;
            pushed = false;

            if (tile.type === 'alarm' || tile.type === 'clear_alarm') {
                this.deleteAlarmWithoutContainer();

                this.tiles.alarm.forEach((val, key) =>{
                    if(val.id === tile.id){
                        pushed = true;
                        if (tile.type === 'clear_alarm') {
                            this.tiles.alarm.splice(key, 1);
                            this.addToDatabase(tile, 'delete');
                        }
                        if (tile.type === 'alarm') {
                            this.tiles.alarm[key] = tile;
                        }
                    }
                });

                if (!pushed && tile.type !== 'clear_alarm') {
                    pushed = true;
                    this.tiles.alarm.push(tile);
                    const tilesData = this.database.tiles.getValue();
                    if (Array.isArray(tilesData)) {
                        this.database.tiles.next(tilesData.concat([tile]));
                    }
                }
            } else if (tile.type === 60 && tile.visibility) {
                if (Array.isArray(this.tiles.container)) {
                    this.tiles.container.forEach((parent: TilesContainer) => {
                        if (parent.values.id === tile.id) {
                            pushed = true;
                            (tile as TileContainer).count = parent.values.count;
                            parent.values = tile as TileContainer;
                        }
                    });

                    if (!pushed && tile.visibility) {
                        pushed = true;
                        (tile as TileContainer).count = 0;
                        this.tiles.container.push({
                            values: tile as TileContainer,
                            elements: []
                        });

                        const tilesData = this.database.tiles.getValue();
                        if (Array.isArray(tilesData)) {
                            this.database.tiles.next(tilesData.concat([tile]));
                        }
                    }
                }
            } else {
                if (Array.isArray(this.tiles.container)) {
                    this.tiles.container.forEach((parent: TilesContainer) => {
                        if (parent.values.id === (tile as Tile).parentId) {
                            parent.elements.forEach((val, key) => {
                                if (tile.id === val.id && tile.visibility) {
                                    pushed = true;
                                    parent.elements[key] = tile as Tile;
                                    this.addToDatabase(tile, 'add');
                                }
                            });
                            if (!pushed && tile.visibility) {
                                pushed = true;
                                parent.elements.push(tile as Tile);
                                parent.values.count++;

                                const tilesData = this.database.tiles.getValue();
                                if (Array.isArray(tilesData)) {
                                    this.database.tiles.next(tilesData.concat([tile]));
                                }
                            }
                        }
                    });
                }

                if(!pushed){
                    this.updateWithoutContainer([tile]);
                }
            }
        });
    }

    private deleteAlarmWithoutContainer() {
        this.tiles.withoutContainer.elements.forEach((val, key) =>{
            // @ts-ignore, val.type === 'clear_alarm'
            if(val.type === 'alarm' || val.type === 'clear_alarm'){
                this.tiles.withoutContainer.elements.splice(key, 1);
            }
        })
    }

    private addToDatabase(tile: TilesData, type: 'add' | 'delete'): void {
        const oldTilesData = this.database.tiles.getValue();

        if (Array.isArray(oldTilesData)) {
            let pushed = false;

            oldTilesData.forEach((val, key) => {
                if (val.id === tile.id) {
                    pushed = true;
                    if (type === 'add') {
                        oldTilesData[key] = tile;
                    } else if (type === 'delete') {
                        oldTilesData[key].splice(key, 1);
                    }
                }
            });

            if (!pushed) {
                oldTilesData.push(tile);
            }

            this.database.tiles.next(oldTilesData);
        }
    }

    private updateMenu() {
        let pushed = false;

        this.menuData.forEach(val => {
            if (Object.keys(val).length) {
                pushed = false;

                if(Array.isArray(this.menu.allElementMenu)) {
                    this.menu.allElementMenu.forEach((value, key) => {
                        if (val.id === value.id && val !== value && val.id !== 0) {
                            pushed = true;

                            if (!(val.id === this.control.setsNewValue && val.duringChange === 't')
                                || val.id !== this.control.setsNewValue) {
                                    const tempMenuType = this.menu.allElementMenu[key].menuType;
                                    const tempVisibility = this.menu.allElementMenu[key].access;
                                    const tempParentId = this.menu.allElementMenu[key].parentId;

                                    this.menu.allElementMenu[key] = _.cloneDeep(val);

                                    this.menu.allElementMenu[key].access = val.parentId !== value.parentId
                                        ? tempVisibility
                                        : this.menu.allElementMenu[key].access;
                                    this.menu.allElementMenu[key].menuType = tempMenuType;
                                    this.menu.allElementMenu[key].parentId = tempParentId;

                                    this.menu.setMainMenu();
                                    this.checkTilesForUpdatedMenuElement(val);

                                if (this.uiRouterGlobals.current.name === 'control') {
                                    if (Array.isArray(this.menu.submenu) && this.menu.submenu.length > 0) {
                                        this.menu.mainMenu[this.menu.submenu[0].menuType].isVisible = true;

                                        if (this.menu.search.txt && this.menu.search.txt.length) {
                                            this.menu.searchText(this.menu.search.txt, this.menu.mainMenu[val.menuType]);
                                        } else {
                                            this.menu.getMenuChildElement(this.menu.parentElementaa);
                                        }
                                    }

                                    const elementVal = this.control.elementVal.getValue();
                                    if (Object.keys(elementVal).length > 0 && elementVal.id === val.id) {
                                        this.control.element.next(val);
                                        this.setControl(val);
                                    }
                                }
                            }

                            if (val.id === this.control.setsNewValue && val.duringChange === 'f') {
                                this.control.setsNewValue = 0;
                            }
                        }
                    });

                    if (!pushed) {
                        this.menu.allElementMenu.push(val);
                        this.menu.setMainMenu();

                        if (this.uiRouterGlobals.current.name === 'menu') {
                            this.menu.smallBreadcrumbs = window.innerWidth < 1366;

                            if (Array.isArray(this.menu.submenu) && this.menu.submenu.length > 0) {
                                this.menu.mainMenu[this.menu.submenu[0].menuType].isVisible = true;
                            } else {
                                this.menu.mainMenu[val.menuType].isVisible = true;
                            }
                        }
                    }
                }
            }
        });

        this.menu.updatedMenu.next(true);

        if (Array.isArray(this.menu.submenu) && this.menu.submenu.length > 0) {
            this.menuData.forEach((element) => {
                if (Array.isArray(this.menu.submenu)) {
                    const index = this.menu.submenu.findIndex(
                        submenuElement => submenuElement.id === element.id);
                    if (index !== -1 && element.duringChange === 'f') {
                        this.menu.submenu[index] = element;
                    }
                }
            });
        }
    }

    private checkTilesForUpdatedMenuElement(newMenuElement: MenuElement) {
        if (this.tiles.createContainer === false) {
            this.checkControllerWithoutContainers(newMenuElement);
        }
        if (this.tiles.createContainer
            && this.parameters.selectedModuleType !== 'zones_controller'
            && this.parameters.selectedModuleType !== 'zones_system_controller') {
            this.checkControllerWithContainers(newMenuElement);
        }
    }

    private checkControllerWithoutContainers(newMenuElement: MenuElement) {
        const updatedTile = this.tiles.withoutContainer.elements.find(
            tile => tile.menuId === newMenuElement.id
        );
        if (updatedTile) {
            const valCopy = _.cloneDeep(newMenuElement);
            this.menuElementsToTilesUpdate.push(valCopy);
        }
    }

    private checkControllerWithContainers(newMenuElement: MenuElement) {
        if (Array.isArray(this.tiles.container)) {
            this.tiles.container.forEach((container) => {
                container.elements.forEach((tile: Tile) => {
                    if (tile.menuId === newMenuElement.id) {
                        const valCopy = _.cloneDeep(newMenuElement);
                        this.menuElementsToTilesUpdate.push(valCopy);
                    }
                })
            });
        }
    }

    private updateTilesAfterMenuUpdate() {
        if (this.menuElementsToTilesUpdate.length > 0) {
            if (this.tiles.createContainer === false) {
                this.updateTilesInControllerWithoutContainers();
            }

            if (this.tiles.createContainer
                && this.parameters.selectedModuleType !== 'zones_controller'
                && this.parameters.selectedModuleType !== 'zones_system_controller') {
                this.updateTilesInControllerWithContainers();
            }

            this.menuElementsToTilesUpdate = [];
        }
    }

    private updateTilesInControllerWithoutContainers() {
        const tilesToUpdate: (Tile | AlarmTile)[] = [];

        for (const menuElement of this.menuElementsToTilesUpdate) {
            const updatedTile = this.tiles.withoutContainer.elements.find(tile => tile.menuId === menuElement.id);
            if (updatedTile) {
                const tileCopy = _.cloneDeep(updatedTile);
                tileCopy.elementMenu = menuElement;
                tilesToUpdate.push(tileCopy);
            }
        }
        if (tilesToUpdate.length > 0) {
            this.updateWithoutContainer(tilesToUpdate);
        }
    }

    private updateTilesInControllerWithContainers() {
        const tilesToUpdate: Tile[] = [];

        for (const menuElement of this.menuElementsToTilesUpdate) {
            if (Array.isArray(this.tiles.container)) {
                this.tiles.container.forEach((container) => {
                    container.elements.forEach((tile: Tile) => {
                        if (tile.menuId === menuElement.id) {
                            const tileCopy = _.cloneDeep(tile);
                            tileCopy.elementMenu = menuElement;
                            tilesToUpdate.push(tileCopy);
                        }
                    })
                });
            }
        }
        if (tilesToUpdate.length > 0) {
            this.updateContainer(tilesToUpdate);
        }
    }


    private setControl(element: MenuElement) {
        this.control.elementVal.next(element);

        switch (element.type) {
            case 3:
            case 4:
            case 5:
                this.timeDate.setTimeValue();
                break;
            case 7:
                this.control.setTempTime();
                break;
            case 11:
                this.inputBox.getCheckBox();
                break;
            case 12:
                this.schedule.setScheduleTable();
                break;
            case 13:
                this.schedule.onOffGetSet('main');
                break;
            case 15:
                this.inputBox.getSelectBox(element.params.value);
                break;
            case 29:
                this.heatingCurve.createHeatingCurveChart();
                break;
            case 28:
            case 30:
                this.heatingCurve.getHeatingCurve();
                break;
            case 31:
                this.heatingCurve.drawHeatingShift();
                break;
            case 100:
                this.schedule.getMultiTable();
                break;
            case 107:
                this.timeDate.setDatePicker();
                break;
            case 108:
                this.timeDate.setDatePickers();
                break;
            case 120:
                this.scheduleIntervals.setDay(element.params.days[0], 0);
                break;
            default:
                this.control.setControl(element);
        }
    }
}
